/**
 * Group drag for GrapesJS multi-selections.
 *
 * GrapesJS's drag controllers only move the one component the user grabbed,
 * even when several are selected via Shift-click / marquee. This module hooks
 * `component:drag:start` / `component:drag:end` and:
 *
 *   - In absolute drag mode, mirrors every `left`/`top` change on the primary
 *     onto every other selected component (preserving each one's own offset).
 *   - In translate / sort drag mode, when the primary is dropped into a new
 *     parent, re-appends every other selected component into the same parent
 *     immediately after the primary (preserving their original document order).
 *
 * Usage:
 *   const tearDown = setupGroupDrag(editor);
 *   // on unmount:
 *   tearDown();
 */

function parsePx(value) {
  if (value == null) return null;
  const n = parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
}

function getComponentStyle(comp) {
  if (!comp) return {};
  if (typeof comp.getStyle === 'function') {
    const s = comp.getStyle();
    if (s) return s;
  }
  if (typeof comp.get === 'function') {
    return comp.get('style') || {};
  }
  return {};
}

function capturePosition(comp) {
  const style = getComponentStyle(comp);
  return {
    left: parsePx(style.left),
    top: parsePx(style.top),
  };
}

function componentIndex(comp) {
  if (!comp) return 0;
  if (typeof comp.index === 'function') {
    try {
      return comp.index();
    } catch (_e) {
      /* fall through */
    }
  }
  const parent = typeof comp.parent === 'function' ? comp.parent() : null;
  const siblings = parent && typeof parent.components === 'function' ? parent.components() : null;
  if (siblings && typeof siblings.indexOf === 'function') {
    return Math.max(0, siblings.indexOf(comp));
  }
  return 0;
}

function sortByDocumentOrder(components) {
  return components.slice().sort((a, b) => componentIndex(a) - componentIndex(b));
}

function sameComponent(a, b) {
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.cid && b.cid && a.cid === b.cid) return true;
  return false;
}

export function setupGroupDrag(editor) {
  let state = null;
  let onPrimaryStyleChange = null;

  const cleanupState = () => {
    if (state && state.primary && onPrimaryStyleChange && typeof state.primary.off === 'function') {
      state.primary.off('change:style', onPrimaryStyleChange);
    }
    state = null;
    onPrimaryStyleChange = null;
  };

  const beginGroupDrag = (primary) => {
    if (!primary) return null;
    const all = typeof editor.getSelectedAll === 'function' ? editor.getSelectedAll() : [];
    if (!all || all.length <= 1) return null;
    if (!all.some((c) => sameComponent(c, primary))) return null;

    const others = all.filter((c) => !sameComponent(c, primary));
    if (others.length === 0) return null;

    return {
      primary,
      others,
      primaryInitial: capturePosition(primary),
      othersInitial: new Map(others.map((c) => [c.cid, capturePosition(c)])),
      primaryInitialParent:
        typeof primary.parent === 'function' ? primary.parent() : null,
      lastApplied: { dx: 0, dy: 0 },
    };
  };

  const applyDeltaToOthers = () => {
    if (!state) return;
    const cur = capturePosition(state.primary);
    // If we haven't yet captured a starting position (e.g. primary had no
    // explicit left/top before the drag), treat the first observed value as
    // the baseline.
    if (state.primaryInitial.left == null && cur.left != null) {
      state.primaryInitial.left = cur.left;
    }
    if (state.primaryInitial.top == null && cur.top != null) {
      state.primaryInitial.top = cur.top;
    }

    const dx =
      cur.left != null && state.primaryInitial.left != null
        ? cur.left - state.primaryInitial.left
        : 0;
    const dy =
      cur.top != null && state.primaryInitial.top != null
        ? cur.top - state.primaryInitial.top
        : 0;

    if (dx === 0 && dy === 0) return;
    if (dx === state.lastApplied.dx && dy === state.lastApplied.dy) return;
    state.lastApplied = { dx, dy };

    state.others.forEach((c) => {
      const init = state.othersInitial.get(c.cid);
      if (!init) return;
      // Skip components that aren't absolute-positioned — moving them by
      // left/top wouldn't make sense in flow layout.
      if (init.left == null && init.top == null) return;

      const update = {};
      if (init.left != null) update.left = `${init.left + dx}px`;
      if (init.top != null) update.top = `${init.top + dy}px`;

      if (typeof c.addStyle === 'function') {
        c.addStyle(update);
      } else if (typeof c.setStyle === 'function') {
        c.setStyle({ ...(getComponentStyle(c) || {}), ...update });
      }
    });
  };

  const handleDragStart = (info = {}) => {
    cleanupState();
    const primary = info.target || info.component || info.model;
    const next = beginGroupDrag(primary);
    if (!next) return;
    state = next;
    onPrimaryStyleChange = () => applyDeltaToOthers();
    if (typeof primary.on === 'function') {
      primary.on('change:style', onPrimaryStyleChange);
    }
  };

  const handleDragEnd = (info = {}) => {
    if (!state) return;
    const primary = info.target || info.component || info.model || state.primary;
    if (!sameComponent(primary, state.primary)) {
      cleanupState();
      return;
    }

    // One last delta pass in case the final position wasn't broadcast yet.
    applyDeltaToOthers();

    // Sort / translate drop: if the primary landed in a new parent, move the
    // rest of the selection right after it, in their original relative order.
    const newParent = typeof primary.parent === 'function' ? primary.parent() : null;
    const originalParent = state.primaryInitialParent;
    if (newParent && newParent !== originalParent) {
      const primaryIdx = componentIndex(primary);
      const ordered = sortByDocumentOrder(state.others);
      ordered.forEach((c, i) => {
        try {
          if (typeof newParent.append === 'function') {
            newParent.append(c, { at: primaryIdx + 1 + i });
          }
        } catch (_err) {
          /* ignore reparent failures (e.g. invalid drop target) */
        }
      });

      // Re-establish the multi-selection so toolbar/delete still operate
      // on every moved component after the drop.
      try {
        if (typeof editor.select === 'function') {
          editor.select([primary, ...ordered]);
        }
      } catch (_e) {
        /* ignore selection restore failure */
      }
    }

    cleanupState();
  };

  editor.on('component:drag:start', handleDragStart);
  editor.on('component:drag:end', handleDragEnd);

  return () => {
    editor.off('component:drag:start', handleDragStart);
    editor.off('component:drag:end', handleDragEnd);
    cleanupState();
  };
}
